import { useState, useCallback, useRef } from "react";

// Refresh sound instance (singleton)
let refreshSound = null;

const playRefreshSound = () => {
  try {
    if (!refreshSound) {
      refreshSound = new Audio('/sounds/refresh.wav');
      refreshSound.volume = 0.3;
    }
    refreshSound.currentTime = 0;
    refreshSound.play().catch(() => {}); // Ignore autoplay errors
  } catch (e) {
    // Sound not available, ignore
  }
};

/**
 * ADE Logo Refresh Button
 * Shows ADE logo that spins for 3 seconds when clicked to refresh data
 * Plays a sound on refresh
 */
export const AdeRefreshButton = ({ onRefresh, className = "", playSound = true }) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    
    // Play sound on manual refresh
    if (playSound) {
      playRefreshSound();
    }
    
    try {
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error('Refresh error:', error);
    }
    
    // Spin for minimum 3 seconds
    setTimeout(() => {
      setIsRefreshing(false);
    }, 3000);
  }, [isRefreshing, onRefresh, playSound]);
  
  return (
    <button
      onClick={handleRefresh}
      disabled={isRefreshing}
      className={`p-2 rounded-full hover:bg-gray-100 transition-colors ${className}`}
      title="Aggiorna"
      data-testid="ade-refresh-btn"
    >
      <img
        src="/images/ade-logo-extracted.png"
        alt="ADE"
        className={`w-8 h-8 object-contain transition-transform ${
          isRefreshing ? 'animate-spin-slow' : 'hover:scale-110'
        }`}
      />
    </button>
  );
};

/**
 * Hook for auto-refresh functionality
 * @param refreshFn - Function to call on refresh
 * @param intervalMs - Auto-refresh interval in milliseconds (default: 30000 = 30 seconds)
 * @param enabled - Whether auto-refresh is enabled
 */
export const useAutoRefresh = (refreshFn, intervalMs = 30000, enabled = true) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  
  const refresh = useCallback(async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    try {
      await refreshFn();
      setLastRefresh(Date.now());
    } catch (error) {
      console.error('Auto-refresh error:', error);
    } finally {
      // Keep spinning for at least 3 seconds
      setTimeout(() => setIsRefreshing(false), 3000);
    }
  }, [refreshFn, isRefreshing]);
  
  // Auto-refresh effect
  useState(() => {
    if (!enabled) return;
    
    const interval = setInterval(() => {
      refresh();
    }, intervalMs);
    
    return () => clearInterval(interval);
  }, [enabled, intervalMs, refresh]);
  
  return { isRefreshing, refresh, lastRefresh };
};

export default AdeRefreshButton;
